// Robust text resizing.
//
// `:root { font-size: N% }` only scales text sized in rem/em. Most real sites
// (especially page builders) size text in px, so that rule does nothing for them
// and the "A+/A−" buttons appear broken. This module instead walks every
// text-bearing element, records its ORIGINAL computed px once, and rewrites
// font-size = original * factor — so px text scales too. Restores at 100%.

const ORIG_ATTR = 'data-nag-fs';   // stores the element's original font-size in px
let lastFactor = 1;

function scaleNow(factor: number): void {
  if (!document.body) return;
  const host = document.getElementById('nagisot-host');
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
    acceptNode(node): number {
      const el = node as Element;
      const tag = el.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'SVG') {
        return NodeFilter.FILTER_REJECT;
      }
      if (host && (el === host || host.contains(el))) return NodeFilter.FILTER_REJECT;
      // Only elements that directly contain visible text.
      for (const c of Array.from(el.childNodes)) {
        if (c.nodeType === Node.TEXT_NODE && (c.textContent || '').trim()) return NodeFilter.FILTER_ACCEPT;
      }
      return NodeFilter.FILTER_SKIP;
    },
  });

  const els: HTMLElement[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) els.push(n as HTMLElement);

  for (const el of els) {
    let orig = el.getAttribute(ORIG_ATTR);
    if (orig === null) {
      const px = parseFloat(getComputedStyle(el).fontSize);
      if (!Number.isFinite(px) || px <= 0) continue;
      orig = String(px);
      el.setAttribute(ORIG_ATTR, orig);
    }
    el.style.setProperty('font-size', `${(parseFloat(orig) * factor).toFixed(2)}px`, 'important');
  }
}

/** Restore every element we touched back to its original size. */
export function restoreTextScale(): void {
  document.querySelectorAll<HTMLElement>(`[${ORIG_ATTR}]`).forEach((el) => {
    el.style.removeProperty('font-size');
    el.removeAttribute(ORIG_ATTR);
  });
  lastFactor = 1;
}

/** Apply a text-size percentage (100 = no scaling). Idempotent per factor. */
export function applyTextScale(pct: number): void {
  const factor = (pct || 100) / 100;
  // At 100% ALWAYS restore (cheap + idempotent) — never early-return via the
  // lastFactor guard. The loader and the panel are separate bundles with
  // separate copies of `lastFactor`, so after a reload the loader may have
  // scaled the page while this bundle still thinks lastFactor===1; the guard
  // would then swallow a reset and leave inline font-sizes stuck.
  if (factor === 1) { restoreTextScale(); return; }
  if (factor === lastFactor) return;
  lastFactor = factor;

  const run = (): void => scaleNow(factor);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
}
